(ns dre.widgets.related-concepts.events
  (:require
   [dre.fx :as dre.fx]
   [day8.re-frame.tracing :refer-macros [fn-traced]]
   [dre.network :as-alias network]
   [dre.pages.search.events :as search-events]
   [dre.pages.my-searches.signals.events :as signal-events]
   [dre.utils :refer [stringify-keys-with-namespaces aborted-request?]]
   [dre.shared.interface.search.pills :as pills]
   [re-frame.core :as rf]
   [superstructor.re-frame.fetch-fx :as-alias fetch-fx]))


(def db-path [:widgets :related-concepts])

(def id
  :related-concepts)

(def types
  #{:term :text})

(defn term-pills
  [pills]
  (let [xform (comp (pills/xflatten)
                    (filter (comp types :pill/type)))]
    (vec (eduction xform pills))))

(defn senses-pills
  [pills]
  (let [xform (comp (pills/xflatten)
                    (filter (comp #{:senses} :pill/type)))]
    (vec (eduction xform pills))))


;; -- Events

(rf/reg-event-fx
 ::set-loading
 [(rf/path db-path) rf/trim-v]
 (fn-traced
  [{db :db} _]
  {:db (assoc db :state :loading)}))

(rf/reg-event-fx
  ::init
  [(rf/path db-path) rf/trim-v]
  (fn-traced
    [_ [{:keys [after-fx search-query]}]]
    (let 
     [pills (:query/pills search-query)
      blueprints [(pills/flats->pill pills)]]
     {:db {:state :loading}
     ::network/request-with-abort [{:endpoint :dictionary/complete-blueprints}
                                   {:body (stringify-keys-with-namespaces {:query/blueprints blueprints
                                                                           :dict/pill-details? true})
                                    :on-success [::success-query-details {:after-fx after-fx :search-query search-query}]
                                    :on-failure [::failure {:after-fx after-fx}]}]})))


(rf/reg-event-fx
  ::success-query-details
  [rf/trim-v]
  (fn-traced
    [{db :db} [{:keys [after-fx search-query ::fetch-fx/response]}]]
    (let [pills (pills/coerce (:query/pills (:body response)))
          senses (pills/pills-senses pills true)
          signal-pills (filter pills/signal-pill? (:query/pills search-query))
          signal-pills+signals
          (map (fn [sp]
                 (let [uuid (:pill/query sp)
                       signal (get-in db (cons signal-events/db-path (conj signal-events/uuid->signal-path uuid)))]
                   (assoc sp :signal signal)))
               signal-pills)
          search-query-details
          (if (seq (concat pills signal-pills))
            (assoc search-query
                   :query/pills pills
                   :query/term-pills (term-pills pills)
                   :query/senses-pills (senses-pills pills)
                   :query/signal-pills signal-pills+signals
                   )
            search-query)]
      {::network/request-with-abort [{:endpoint :dictionary/senses}
                                     {:body (stringify-keys-with-namespaces {:dict/senses senses
                                                                             :dict/labels? true})
                                      :on-success [::success-query-senses {:after-fx after-fx :search-query-details search-query-details}]
                                      :on-failure [::failure {:after-fx after-fx}]}]})))

(rf/reg-event-fx
 ::success-query-senses
 [(rf/path db-path) rf/trim-v]
 (fn-traced
  [{db :db} [{:keys [after-fx search-query-details ::fetch-fx/response]}]]
  (let [{:keys [:dict/sense-details :dict/label-map]} (:body response)
        mf (fn [m]
             (let [label (get label-map (keyword (:sense/id m)))]
               {:pill/definitions (:sense/definitions m)
                :pill/canonical-label label
                :pill/label label
                :pill/category (:sense/category m)
                :pill/tuis (:sense/tuis m)}))
        sense-details-map (zipmap (map :sense/id sense-details)
                                  (map mf sense-details))]
    {:db (assoc db :state :success
                :data search-query-details
                :data-senses sense-details-map)
     :dispatch after-fx})))


(rf/reg-event-fx
  ::failure
  [(rf/path db-path) rf/trim-v]
  (fn-traced
    [_ [{:keys [after-fx ::fetch-fx/response] :as opts}]]
    (if (aborted-request? response)
      {}
      {:db {:state :failure
            :data nil}
       :dispatch-n [after-fx
                    [:dre.network/default-failure (merge {:event :related-concepts} opts)]]})))

(rf/reg-event-fx
 ::export
 [(rf/path search-events/db-path) rf/trim-v]
 (fn-traced
  [{db :db} [_]]
  (let [search-query (:search-query db)
        widget-filters (:widget-filters db)
        aq (partial search-events/analysis-query search-query widget-filters)
        aq* (comp first aq)
        query (aq* [])]
    {::network/request
     [{:endpoint :dictionary/export-labels}
      {:body (stringify-keys-with-namespaces {:query/blueprints (:query/blueprints query)})
       :response-content-type :array-buffer
       :response-content-types
       {"text/plain"               :array-buffer
        "application/octet-stream" :array-buffer}
       :on-success [::success-export]}]})))

(rf/reg-event-fx
 ::success-export
 [(rf/path db-path) rf/trim-v]
 (fn-traced
  [_ [{:keys [::fetch-fx/response]}]]
  (rf/dispatch [::dre.fx/download {:content (:body response)
                                   :filename "query-labels.docx"
                                   :mimetype "text/plain;charset=UTF-8"}])
  nil))




;; -- Subscriptions

(rf/reg-sub
  ::state
  (fn [db _]
    (get-in db db-path)))


(rf/reg-sub
  ::status
  :<- [::state]
  (fn [db _]
    (:state db)))

(defn as-list
  ([results]
   (as-list results nil))
  ([results results-senses]
   (let [query-details results
         term-pills (:query/term-pills query-details)
         term-pills (distinct term-pills)
         senses-pills (:query/senses-pills query-details)
         senses-pills (distinct senses-pills)
         signal-pills (:query/signal-pills query-details)
         ->phrase (fn [pill]
                    [{:label (or (:pill/label pill)
                                 (:pill/canonical-label pill)
                                 (:pill/query pill))
                      :sense (or (:pill/query pill) (:pill/sense pill))}])]
     (concat
      (for [[_idx signal-pill] (map-indexed vector signal-pills)
            :let [signal-query (-> signal-pill :signal :query/search-query)]]
        {:type :signal
         :signal-query signal-query
         :term {:query/pills signal-pill}})
      (for [[_idx senses-pill] (map-indexed vector senses-pills)
            sense (concat [nil (:pill/query senses-pill)]
                          (:pill/other-senses senses-pill))]
        (if (nil? sense)
          (let [senses-pill (merge (get results-senses (:pill/query senses-pill))
                                   senses-pill)]
            {:phrase (->phrase senses-pill)
             :pill senses-pill
             :type :term
             :term {:query/pills
                    [(assoc senses-pill
                            :pill/definitions
                            ["A Group of Concepts that share a common label"])]
                    :query/type :articles}})
          (let [excluded-senses (set (:pill/excluded-senses senses-pill))
                pill (merge (get results-senses sense)
                            (select-keys senses-pill [:pill/mode])
                            {:pill/type :term
                             :pill/query sense})
                pill (first (pills/coerce [pill]))]
            {:phrase (->phrase pill)
             :pill pill
             :type :term
             :subtype :senses
             :parent senses-pill
             :excluded (contains? excluded-senses sense)
             :term {:query/pills [pill] :query/type :articles}})))
      (for [[_idx term-pill] (map-indexed vector term-pills)]
        {:phrase (->phrase term-pill)
         :pill (merge (get results-senses (:pill/query term-pill))
                      term-pill)
         :type :term
         :term {:query/pills [term-pill] :query/type :articles}})))))

(rf/reg-sub
 ::data
 :<- [::state]
 (fn [db _]
   (let [results (:data db)
         results-senses (:data-senses db)]
     (as-list results results-senses))))